import { createStyles } from 'antd-style'

export type AccentVariant = 'purple' | 'green' | 'yellow' | 'red' | 'neutral'

const ACCENT_COLORS: Record<
  AccentVariant,
  { stripe: string; surface: string; text: string; border: string }
> = {
  purple: {
    stripe: '#9254de',
    surface: '#f9f0ff',
    text: '#531dab',
    border: '#d3adf7'
  },
  green: {
    stripe: '#52c41a',
    surface: '#f6ffed',
    text: '#237804',
    border: '#b7eb8f'
  },
  yellow: {
    stripe: '#faad14',
    surface: '#fffbe6',
    text: '#ad6800',
    border: '#ffe58f'
  },
  red: {
    stripe: '#ff4d4f',
    surface: '#fff1f0',
    text: '#a8071a',
    border: '#ffa39e'
  },
  neutral: {
    stripe: '#bfbfbf',
    surface: '#fafafa',
    text: '#262626',
    border: '#d9d9d9'
  }
}

export const getAccentColors = (variant: AccentVariant) =>
  ACCENT_COLORS[variant]

export const useStyles = createStyles(
  ({ token }, { variant }: { variant: AccentVariant }) => {
    const colors = ACCENT_COLORS[variant]

    return {
      card: {
        flex: 1,
        minWidth: 0,
        borderRadius: token.borderRadiusLG,
        background: token.colorBgContainer,
        border: `1px solid ${colors.border}`,
        borderTop: `3px solid ${colors.stripe}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      },
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px 6px'
      },
      title: {
        fontSize: 14,
        fontWeight: 600,
        color: token.colorTextHeading,
        margin: 0,
        lineHeight: 1.2
      },
      date: {
        fontSize: 11,
        color: token.colorTextSecondary,
        whiteSpace: 'nowrap'
      },
      body: {
        flex: 1,
        padding: '8px 12px',
        background: colors.surface,
        color: colors.text,
        fontSize: 13,
        fontWeight: 500,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight: 56
      },
      statusText: {
        wordBreak: 'break-word'
      },
      actionRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 8,
        marginTop: 'auto'
      },
      statusTag: {
        alignSelf: 'flex-start',
        fontWeight: 600,
        letterSpacing: '0.5px'
      },
      footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        fontSize: 11,
        color: token.colorTextTertiary,
        background: token.colorBgContainer
      },
      footerText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '50%'
      },
      detailList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontWeight: 400
      },
      detailRow: {
        display: 'flex',
        gap: 6,
        fontSize: 12,
        lineHeight: 1.4
      },
      detailLabel: {
        color: token.colorTextSecondary,
        flexShrink: 0,
        minWidth: '40%',
        wordBreak: 'break-word'
      },
      detailValue: {
        color: colors.text,
        fontWeight: 500,
        wordBreak: 'break-word',
        minWidth: 0,
        flex: 1
      }
    }
  }
)
